import React from 'react';
import { Select, MenuItem, Chip, useTheme, Box } from '@mui/material';

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'ARCHIVED', label: 'Archived' },
];

const STATUS_COLOR_ALIASES: Record<string, string> = {
  SUCCESS: 'SUCCESS',
  COMPLETED: 'SUCCESS',

  RUNNING: 'RUNNING',
  ACTIVE: 'RUNNING',
  AVAILABLE: 'SUCCESS',
  UNDER_NURTURING: 'RUNNING',
  RESTARTING: 'RUNNING',

  FAILED: 'FAILED',
  FAILURE: 'FAILED',
  INACTIVE: 'FAILED',
  UPSTREAM_FAILED: 'FAILED',

  PENDING: 'PENDING',
  SHUTDOWN: 'PENDING',
  DEFERRED: 'PENDING',
  QUEUED: 'PENDING',
  SCHEDULED: 'PENDING',
  UP_FOR_RESCHEDULE: 'PENDING',
  UP_FOR_RETRY: 'PENDING',

  SKIPPED: 'SKIPPED',
  ARCHIVED: 'NO_STATUS',
  NOT_UNDER_NURTURING: 'NO_STATUS',
  REMOVED: 'SKIPPED',

  NOT_AVAILABLE: 'NO_STATUS',
  NO_STATUS: 'NO_STATUS',
};

const getCustomColors = (theme: any): Record<string, string> => ({
  SUCCESS: theme.palette.statusChip?.success ?? theme.palette.success.main,
  FAILED: theme.palette.statusChip?.failed ?? theme.palette.error.main,
  RUNNING: theme.palette.statusChip?.running ?? theme.palette.info.main,
  PENDING: theme.palette.statusChip?.pending ?? theme.palette.warning.main,
  SKIPPED: theme.palette.statusChip?.skipped ?? theme.palette.secondary.main,
  NO_STATUS:
    theme.palette.statusChip?.noStatus ??
    (theme.palette.mode === 'dark' ? theme.palette.grey[700] : theme.palette.grey[300]),
});

const greyTextStatuses = new Set(['NO_STATUS']);

interface CellStatusDropdownProps {
  params: any;
  onClick: (params: any, clickedElement: string, extraData?: string) => void;
  children?: React.ReactNode;
}

const StatusChip = ({ value, theme }: { value: string; theme: any }) => {
  const noStatusTextColor = theme.palette.mode === 'dark' ? theme.palette.grey[200] : theme.palette.grey[700];
  const key = value ? value.toUpperCase().replace(/\s+/g, '_') : 'NO_STATUS';
  const normalizedKey = STATUS_COLOR_ALIASES[key] ?? 'NO_STATUS';
  const customColors = getCustomColors(theme);
  const backgroundColor = customColors[normalizedKey] || customColors.NO_STATUS;
  const label = key === 'ACTIVE' ? 'Active' : key === 'ARCHIVED' ? 'Archived' : value;
  const textColor = greyTextStatuses.has(normalizedKey)
    ? noStatusTextColor
    : theme.palette.common.white;

  return (
    <Chip
      label={label}
      size="small"
      sx={{
        backgroundColor,
        color: textColor,
        fontWeight: 500,
      }}
    />
  );
};

const CellStatusDropdown = ({ params, onClick }: CellStatusDropdownProps) => {
  const theme = useTheme();
  const value = params?.row?.status ?? '';

  const handleChange = (event: { target: { value: string } }) => {
    const newStatus = event.target.value;
    if (newStatus && params) {
      onClick(params, 'status_change', newStatus);
    }
  };

  return (
    <Box onClick={(e) => e.stopPropagation()} sx={{ minWidth: 100 }}>
      <Select
        value={value}
        onChange={handleChange}
        displayEmpty
        size="small"
        renderValue={(selected) => (
          <StatusChip value={selected || ''} theme={theme} />
        )}
        sx={{
          '& .MuiSelect-select': { py: 0.5, display: 'flex', alignItems: 'center' },
          '.MuiOutlinedInput-notchedOutline': { border: 0 },
          '&.MuiOutlinedInput-root:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: 'divider',
          },
        }}
      >
        {STATUS_OPTIONS.map((opt) => (
          <MenuItem key={opt.value} value={opt.value}>
            <StatusChip value={opt.value} theme={theme} />
          </MenuItem>
        ))}
      </Select>
    </Box>
  );
};

export default CellStatusDropdown;
